import axios from "axios";
import { add_list_api, delete_list_api, MetaMask_Verify, moralisGetAllNFTs, MIDDILEWARE_SERVER_URL, subscribeNewsletterApi } from "./Utils/Url";
import { WebConfig } from "./Config/WebConfig";

// Wallet auth: no tokens
export const accessToken = null;

// Attach wallet-signature headers automatically
export function setWalletAuthHeaders(instance) {
  instance.interceptors.request.use((config) => {
    try {
      const walletAddress = window.localStorage.getItem("accountId");
      const raw = walletAddress ? window.localStorage.getItem(walletAddress) : null;
      if (walletAddress && raw) {
        const parsed = JSON.parse(raw);
        const signature = parsed.signature;
        const message = parsed.message;
        const timestamp = parsed.timestamp;
        if (!signature || !message || !timestamp) return config;
        // Some browsers reject CR/LF in raw headers; only send base64 variant
        config.headers = {
          ...(config.headers || {}),
          'x-wallet-address': walletAddress.toLowerCase(),
          'x-wallet-signature': signature,
          // Send only base64 variant to avoid invalid header chars
          'x-signed-message-b64': btoa(unescape(encodeURIComponent(message))),
          'x-signature-timestamp': timestamp,
        };
      }
    } catch (_) {}
    return config;
  });
}

export const metamaskLoginVerification = (data) => {
  const client = axios.create();
  setWalletAuthHeaders(client);
  return client.post(MetaMask_Verify, data, {
    headers: { 'Content-Type': 'application/json' }
  });
};

export const requestMetaMaskNonce = async (address) => {
  try {
    const response = await fetch('/wallet/nonce', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ address }),
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.statusText}`);
    }

    const data = await response.json();
    return data.nonce; // The nonce string used for signing
  } catch (error) {
    console.error('Failed to request nonce:', error);
    throw error;
  }
}

// Function to fetch NFTs from your Node.js API
export const fetchNFTs = async (address, chain) => {
  try {
    // Use the GeoTag contract address (actual minting contract)
    const tokenAddress = WebConfig?.digitalLandAddress;
    
    // Ensure a valid, non-expired wallet signature exists before calling the server
    const walletAddress = window.localStorage.getItem('accountId');
    const raw = walletAddress ? window.localStorage.getItem(walletAddress) : null;
    if (!walletAddress || !raw) {
      const err = new Error('Wallet signature required');
      err.code = 'signature_required';
      throw err;
    }
    try {
      const { timestamp } = JSON.parse(raw);
      const maxAge = (WebConfig?.walletAuth?.signatureMaxAgeMs) || (2 * 60 * 60 * 1000);
      if (!Number.isFinite(Number(timestamp)) || Date.now() - Number(timestamp) > maxAge) {
        const err = new Error('Wallet signature expired');
        err.code = 'signature_expired';
        throw err;
      }
    } catch (_) {
      const err = new Error('Invalid wallet signature payload');
      err.code = 'signature_required';
      throw err;
    }

    // Prepare client with auth headers
    const client = axios.create();
    setWalletAuthHeaders(client);
    const response = await client.get(moralisGetAllNFTs, {
      params: { address, tokenAddress, chain },
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    // If authentication fails, provide helpful error message
    if (error.code === 'signature_required' || error.code === 'signature_expired' || (error.response && error.response.status === 401)) {
      console.error("Authentication required for NFT data access");
      throw new Error("Please log in to access your NFT data");
    }
    throw error;
  }
};

export const addList = (data) => {
  const client = axios.create();
  setWalletAuthHeaders(client);
  return client
    .post(add_list_api, data.data, { headers: { 'Content-Type': 'application/json' } })
    .then((res) => {
      return res;
    })
    .catch((error) => {
      // Handle error if needed
    });
};

export const deleteList = (listId) => {
  const client = axios.create();
  setWalletAuthHeaders(client);
  return client
    .delete(`${delete_list_api}/${listId}`, { headers: { 'Content-Type': 'application/json' } })
    .then((res) => {
      return res;
    })
    .catch((error) => {
      console.error("Error deleting listing:", error);
      throw error;
    });
};

// Add the newsletter subscription function
export const subscribeNewsletter = async (email) => {
  try {
    // For demo purposes, we'll use localStorage to simulate subscription storage
    // This way the subscription "works" without a backend
    const subscribers = JSON.parse(localStorage.getItem('newsletter_subscribers') || '[]');
    
    // Check if email is already subscribed
    if (subscribers.includes(email)) {
      return { success: true, message: "You're already subscribed!" };
    }
    
    // Add email to subscribers list
    subscribers.push(email);
    localStorage.setItem('newsletter_subscribers', JSON.stringify(subscribers));
    
    // Log for debugging
    console.log("Subscription successful using client-side storage:", email);
    
    // Return success response
    return { success: true, message: "Subscription successful" };
    
    /* 
    // Uncomment this when the backend API is ready
    const response = await axios.post(subscribeNewsletterApi, { email });
    return response.data;
    */
  } catch (error) {
    console.error("Newsletter subscription error:", error);
    throw error;
  }
};